import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Eye, ZoomIn, ZoomOut, Minus } from 'lucide-react';
import { useUIScale, UIScale } from '../contexts/UIScaleContext';
import { cn } from './ui/utils';

export const UIScaleToggle: React.FC = () => {
  const { scale, setScale } = useUIScale();

  const scaleOptions: { value: UIScale; label: string; icon: React.ReactNode; description: string }[] = [
    {
      value: 'small',
      label: 'Compact',
      icon: <ZoomOut className="w-4 h-4" />,
      description: 'Smaller text and spacing'
    },
    {
      value: 'normal',
      label: 'Normal',
      icon: <Minus className="w-4 h-4" />,
      description: 'Default size'
    },
    {
      value: 'large',
      label: 'Large',
      icon: <ZoomIn className="w-4 h-4" />,
      description: 'Larger text for better visibility'
    }
  ];

  const currentOption = scaleOptions.find(option => option.value === scale);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2 gap-2 text-xs"
        >
          <Eye className="w-3 h-3" />
          <span className="hidden sm:inline">Text Size</span>
          <Badge variant="secondary" className="text-xs px-1 py-0 h-4">
            {currentOption?.label}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">
          <div className="flex items-center gap-2">
            <Eye className="w-3 h-3" />
            Accessibility - Text Size
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {scaleOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => setScale(option.value)}
            className={cn(
              "flex items-center gap-3 cursor-pointer",
              scale === option.value && "bg-accent"
            )}
          >
            <div className="flex items-center gap-2 flex-1">
              {option.icon}
              <div>
                <div className="font-medium text-sm">{option.label}</div>
                <div className="text-xs text-muted-foreground">{option.description}</div>
              </div>
            </div>
            {scale === option.value && (
              <Badge variant="default" className="text-xs">
                Active
              </Badge>
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          💡 Choose "Large" for better visibility
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};